import { Box, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";
import { Cartao, PilulaStatus } from "../../../../core/styles/superficies";
import { TipoMarketplace, TipoMarketplaceCor } from "../../../../marketplace/enum/TipoMarketplace";
import { StatusPedido, StatusPedidosCores } from "../../../enum/StatusPedido";
import { IPedido } from "../../../interface/IPedido";

interface CabecalhoPedidoProps {
    pedido: IPedido;
    origem: string;
    /** Linhas curtas de contexto: número diário, emissão, previsão. Já formatadas. */
    metadados: string[];
    compacto: boolean;
    /** Botões de ação — mudar status, editar, imprimir. */
    acoes: ReactNode;
}

/** O status chega ora como número, ora como descrição, dependendo de quem montou o pedido. */
const descreverStatus = (status: IPedido["status"]): string =>
    typeof status === "number" ? StatusPedido[status] : String(status);

/**
 * Identidade do pedido: número, status e origem numa linha; contexto logo abaixo.
 *
 * Antes as três coisas — dados, botão de status e chips — disputavam a mesma linha com
 * `space-between`, e a posição de cada uma variava conforme o conteúdo. Aqui a identificação
 * fica sempre à esquerda e as ações à direita, e a faixa lateral repete a cor do status para
 * que a situação do pedido seja lida antes mesmo do texto.
 */
export const CabecalhoPedido = ({ pedido, origem, metadados, compacto, acoes }: CabecalhoPedidoProps) => {
    const status = descreverStatus(pedido.status);
    const cores: Record<string, string> = StatusPedidosCores;
    // Cinza para status desconhecido: pedidos antigos podem ter valores fora do enum atual.
    const corStatus = cores[status] ?? "#9e9e9e";

    return (
        <Cartao
            sx={{
                padding: 2,
                gap: 1.25,
                borderLeft: `4px solid ${corStatus}`,
                backgroundImage: theme =>
                    `linear-gradient(90deg, ${alpha(corStatus, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0)} 45%)`,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: compacto ? "flex-start" : "center",
                    justifyContent: "space-between",
                    flexDirection: compacto ? "column" : "row",
                    gap: 1.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
                    <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em" }}>
                        <Box component="span" sx={{ color: "text.disabled", fontWeight: 600 }}>#</Box>
                        {pedido.numeroPedido}
                    </Typography>
                    <PilulaStatus cor={corStatus}>{status}</PilulaStatus>
                    <Chip
                        size="small"
                        label={origem}
                        sx={{
                            height: 24,
                            color: "#fff",
                            fontWeight: 600,
                            backgroundColor: TipoMarketplaceCor[pedido.marketplace?.tipo ?? TipoMarketplace.PDV],
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        width: compacto ? "100%" : "auto",
                    }}
                >
                    {acoes}
                </Box>
            </Box>

            {/* Contexto secundário numa linha só, para não competir com o número do pedido. */}
            {!!metadados.length && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.8125rem", display: "flex", flexWrap: "wrap", gap: 0.75 }}
                >
                    {metadados.map((texto, indice) => (
                        <Box component="span" key={texto} sx={{ display: "inline-flex", gap: 0.75 }}>
                            {indice > 0 && <Box component="span" sx={{ opacity: 0.45 }}>·</Box>}
                            {texto}
                        </Box>
                    ))}
                </Typography>
            )}
        </Cartao>
    );
};
